import React, { forwardRef } from 'react'
import NextLink, { LinkProps as NextLinkProps } from 'next/link'

import { Button, ButtonProps } from '../Button'

export interface BaseLinkButtonProps extends ButtonProps {
  isDisabled?: boolean
  isExternal?: boolean
  href?: string
}

export const BaseLinkButton = forwardRef(
  ({ isDisabled, onClick, ...restProps }: BaseLinkButtonProps, ref) => (
    <Button
      as={isDisabled ? 'button' : 'a'}
      isDisabled={isDisabled}
      onClick={isDisabled ? event => event.preventDefault() : onClick}
      ref={ref}
      {...restProps}
    />
  ),
)

/////////////////////////////////////////////////////////////////////////////////////////////////

export interface LinkButtonProps extends Omit<ButtonProps, 'href'>, Omit<NextLinkProps, 'as'> {
  hrefAs?: NextLinkProps['as']
  isExternal?: boolean
  isDisabled?: boolean
}

export const LinkButton = forwardRef(
  (
    {
      href,
      hrefAs,
      passHref = true,
      replace,
      scroll,
      shallow,
      prefetch,
      isExternal,
      ...restProps
    }: LinkButtonProps,
    ref,
  ) => {
    const externalProps = isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : null

    if (isExternal) {
      return <BaseLinkButton ref={ref} href={href as string} {...externalProps} {...restProps} />
    }

    return (
      <NextLink
        href={href}
        as={hrefAs}
        passHref={passHref}
        replace={replace}
        scroll={scroll}
        shallow={shallow}
        prefetch={prefetch}
      >
        <BaseLinkButton ref={ref} {...externalProps} {...restProps} />
      </NextLink>
    )
  },
)

export default LinkButton
